import { Box, OutlinedInput, styled } from '@mui/material';
import { useEffect, useRef, useState, type ClipboardEvent, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';

const CharacterInput = styled(OutlinedInput)(({ theme }) => ({
  width: theme.spacing(5),
  height: theme.spacing(7),
  fontSize: '2.5rem',
  '& input': {
    textAlign: 'center',
    textTransform: 'uppercase',
    padding: 0,
  },
  [theme.breakpoints.up('md')]: {
    width: theme.spacing(9),
    height: theme.spacing(11),
    fontSize: '4rem',
  },
}));

export interface CodeInputProps {
  code?: string;
  disabled?: boolean;
  /** Called with the code once all six characters are valid, and with undefined when it stops being so. */
  onChange: (code: string | undefined) => void;
}

export const validateCode = (code: string | null) =>
  typeof code === 'string' && /^[A-F0-9]{6}$/i.test(code) ? code : undefined;

const initialState = ['', '', '', '', '', ''];
const indices = [0, 1, 2, 3, 4, 5] as const;

const hexCharacters = (text: string) => text.toUpperCase().replace(/[^A-F0-9]/g, '');

export const CodeInput = ({ code, disabled = false, onChange }: CodeInputProps) => {
  const [state, setState] = useState(code?.split('') ?? initialState);
  const stateRef = useRef(state);
  stateRef.current = state;

  const { t } = useTranslation();
  const input0 = useRef<HTMLInputElement>(null);
  const input1 = useRef<HTMLInputElement>(null);
  const input2 = useRef<HTMLInputElement>(null);
  const input3 = useRef<HTMLInputElement>(null);
  const input4 = useRef<HTMLInputElement>(null);
  const input5 = useRef<HTMLInputElement>(null);
  const refs = [input0, input1, input2, input3, input4, input5] as const;

  // The parent clearing a complete code (Try Again) empties the boxes. A code that went
  // undefined because the user deleted a character keeps the rest of what they typed.
  useEffect(() => {
    if (code !== undefined || !validateCode(stateRef.current.join(''))) return;

    setState(initialState);
    input0.current?.focus();
  }, [code, input0]);

  const lastReported = useRef<string | undefined>(code);
  useEffect(() => {
    const current = validateCode(state.join(''));
    if (current === lastReported.current) return;
    lastReported.current = current;
    onChange(current);
  }, [onChange, state]);

  // Writes `text` into the boxes from `start` on and moves focus to the box after the last one filled.
  const fill = (start: number, text: string) => {
    const characters = hexCharacters(text)
      .slice(0, indices.length - start)
      .split('');
    if (characters.length === 0) return;
    const newState = state.slice();
    characters.forEach((character, offset) => {
      newState[start + offset] = character;
    });
    setState(newState);
    const next = Math.min(start + characters.length, indices.length - 1);
    refs[next]?.current?.focus();
  };

  const clear = (index: number) => {
    const newState = state.slice();
    newState[index] = '';
    setState(newState);
  };

  const onPaste = (index: number) => (event: ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    fill(index, event.clipboardData.getData('text'));
  };

  const onKeyDown = (index: number) => (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Backspace' || state[index] !== '' || index === 0) return;
    event.preventDefault();
    clear(index - 1);
    refs[index - 1]?.current?.focus();
  };

  return (
    <Box
      aria-label={t('pages.devices.activate.code_label', { total: indices.length })}
      role="group"
      sx={{
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 2,
      }}
    >
      {indices.map((index) => (
        <CharacterInput
          inputProps={{
            'aria-label': t('pages.devices.activate.code_character', {
              total: indices.length,
              position: index + 1,
            }),
            autoComplete: index === 0 ? 'one-time-code' : 'off',
            inputMode: 'text',
          }}
          inputRef={refs[index]}
          disabled={disabled}
          key={index}
          value={state[index]}
          onChange={(event) => {
            const { value } = event.target;
            if (value === '') return clear(index);
            // Typing into a filled box, or an autofilled one-time code, lands more than one character.
            fill(index, value.length > 1 ? value.replace(state[index] ?? '', '') : value);
          }}
          onKeyDown={onKeyDown(index)}
          onPaste={onPaste(index)}
        />
      ))}
    </Box>
  );
};
